import React, {useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";
import {IError} from "../../../../../app/interfaces/response/IResponse";
import {useOfferApi} from "../../../../../app/api/offer";
import {isAxiosError} from "axios";
import {ResponseResultCode} from "../../../../../app/enums/ResponseResultCode";
import {toast} from "react-toastify";
import ToastLayout from "../../../../../components/uiLayouts/ToastLayout/ToastLayout";
import FormLoadingScreen from "../../../../../components/ui/loaders/FormLoadingScreen/FormLoadingScreen";
import {BLErrorCode} from "../../../../../app/enums/BLErrorCode";
import Alert from "../../../../../components/ui/Alert/Alert";
import Button, {ButtonType} from "../../../../../components/ui/Button/Button";
import {IAddBetGetAction} from "../../../../../app/interfaces/offer/IAddBetGetAction";
import {IAddBetPostAction} from "../../../../../app/interfaces/offer/IAddBetPostAction";
import ProposeRateForm from "./ProposeRateForm/ProposeRateForm";
import {useAppSelector} from "../../../../../app/store";
import {selectCurrentUser} from "../../../../../features/authentication/authenticationSliceSelectors";

type ProposeRateProps = {
    id: string;
    close?: () => void;
};

const alertStyle: React.CSSProperties = {
    fontSize: '13px',
    fontWeight: 'bold',
    textAlign: 'center',
    padding: '10px 5px'
}

const ProposeRate: React.FC<ProposeRateProps> = ({
                                                     id,
                                                     close
                                                 }) => {
    const {t} = useTranslation();
    const user = useAppSelector(selectCurrentUser);

    const [disabled, setDisabled] = useState<boolean>(false);
    const [model, setModel] = useState<IAddBetGetAction | null>(null);
    const [errors, setErrors] = useState<Array<IError>>([]);

    const {
        getAddBetData: {
            query: getAddBetData,
            isLoading: getAddBetDataIsLoading,
            cancel: cancelGetAddBetData
        },
        addBet: {
            mutation: addBet,
            cancel: cancelAddBet
        },
        getAddBetDataAnonymous: {
            query: getAddBetDataAnonymous,
            isLoading: getAddBetDataAnonymousIsLoading,
            cancel: cancelGetAddBetDataAnonymous
        },
        addBetAnonymous: {
            mutation: addBetAnonymous,
            cancel: cancelAddBetAnonymous
        }
    } = useOfferApi();

    useEffect(() => {
        if (!id || id === '') {
            if (close) {
                close()
            } else {
                return;
            }
        }

        (async () => {
            try {
                const response = user
                    ? await getAddBetData(id)
                    : await getAddBetDataAnonymous(id);

                if (response?.status === 200 && response.data && response.data.data && response.data.data) {
                    setModel(response.data.data);
                }
            } catch (err) {
                if (isAxiosError(err)) {
                    if (err.response && err.response.data && err.response.status === 422 &&
                        err.response.data.resultCode === ResponseResultCode.NotValidData &&
                        err.response.data.errors && Array.isArray(err.response.data.errors)) {
                        setModel(err.response.data.data);
                        setErrors(err.response.data.errors);
                    } else if (close) {
                        close();
                        toast.error(err.message);
                    }
                }
            }
        })();

        return () => {
            cancelGetAddBetData();
            cancelAddBet();

            cancelGetAddBetDataAnonymous();
            cancelAddBetAnonymous();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const save = async (data: IAddBetPostAction): Promise<void> => {
        setDisabled(prev => !prev);

        try {
            const response = user
                ? await addBet(data)
                : await addBetAnonymous(data);

            if (response?.status === 200 && response.data.resultCode === ResponseResultCode.Ok) {
                if (close) {
                    close();
                }

                document.dispatchEvent(new CustomEvent('onGridMessage', {
                    detail: {
                        action: 'updateRow',
                        rows: [
                            {
                                id: id,
                                isRowLoading: true
                            }
                        ]
                    }
                }));

                toast.success(<ToastLayout text={t("shared.labels.delayed-update-warning")}
                                           title={`${t("shared.labels.offer")} ${model?.orderReleaseGid}`}/>);
            }
        } catch (err) {
            if (isAxiosError(err)) {
                if (err.response && err.response.data && err.response.status === 422 &&
                    err.response.data.resultCode === ResponseResultCode.NotValidData &&
                    err.response.data.errors && Array.isArray(err.response.data.errors)) {
                    setErrors(err.response.data.errors);
                } else if (close) {
                    close();
                    toast.error(err.message);
                }
            }
        }

        setDisabled(prev => !prev);
    }

    if (getAddBetDataIsLoading || getAddBetDataAnonymousIsLoading || model === null) {
        return (
            <FormLoadingScreen height={`200px`} style={{padding: '5px'}}/>
        )
    }

    return (
        <div className={'h100 w100'}>
            <div className={'dialog-default-content-container'}>
                {errors.length > 0
                    ? errors.map((err, index) => {
                        switch (err.errorCode) {
                            case BLErrorCode.InnerError:
                                return (
                                    <Alert type={'Error'}
                                           id={`propose-rate-error-${index}`}
                                           style={alertStyle}
                                           key={`${err.errorCode}-${err.description}`}>
                                        {`${err.description}`}
                                    </Alert>
                                );
                        }

                        return null;
                    })
                    : null
                }

                <ProposeRateForm model={model}
                                 disabled={disabled}
                                 save={save}/>
            </div>
            <div className={'dialog-default-buttons-container'}>
                {close &&
                    <Button buttonType={ButtonType.Transparent}
                            disabled={disabled}
                            className={'close-button'}
                            onClick={close}>
                        {t("shared.btn.cancel")}
                    </Button>
                }
                <Button buttonType={ButtonType.Primary}
                        disabled={disabled}
                        form='propose-rate-offer-form'
                        type='submit'
                        style={{minWidth: '150px'}}
                        className={'confirm-button'}
                        isLoading={disabled}>
                    {t("shared.btn.send")}
                </Button>
            </div>
        </div>
    );
};

export default ProposeRate;